import { useState, useEffect, useCallback, useRef } from 'react';
import { HighlightedText } from '../components/CookingTermPopover';
import { COOKING_TERMS, TERM_KEYS } from '../data/cookingTerms';

// Extract timer from instruction text (returns seconds)
function extractTimer(text) {
  if (!text) return null;

  // Match hours
  const hourMatch = text.match(/(\d+)\s*hours?/i);
  if (hourMatch) {
    return parseInt(hourMatch[1]) * 3600;
  }

  // Match minutes
  const minMatch = text.match(/(\d+)\s*min(ute)?s?/i);
  if (minMatch) {
    return parseInt(minMatch[1]) * 60;
  }

  // Match seconds
  const secMatch = text.match(/(\d+)\s*sec(ond)?s?/i);
  if (secMatch) {
    return parseInt(secMatch[1]);
  }

  return null;
}

// Format seconds to MM:SS or HH:MM:SS
function formatTime(seconds) {
  if (seconds >= 3600) {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  }
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function CookingMode({ recipe, onClose }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState(new Set());
  const [timers, setTimers] = useState({});
  const [isMuted, setIsMuted] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [voiceSupported, setVoiceSupported] = useState(false);
  const [lastCommand, setLastCommand] = useState('');
  const [explainingTerm, setExplainingTerm] = useState(null);
  const [voicesReady, setVoicesReady] = useState(false);
  const recognitionRef = useRef(null);
  const wakeLockRef = useRef(null);
  const hasSpokenRef = useRef(false);
  const isSpeakingRef = useRef(false); // Track when TTS is active to prevent recognition restart

  // Find terms that exist in the current instruction
  const findTermsInText = useCallback((text) => {
    if (!text) return [];
    const found = [];
    const lowerText = text.toLowerCase();
    for (const key of TERM_KEYS) {
      const regex = new RegExp(`\\b${key}\\b`, 'i');
      if (regex.test(lowerText)) {
        found.push(key);
      }
    }
    return found;
  }, []);

  // Extract a cooking term from speech transcript
  const extractTermFromSpeech = useCallback((transcript) => {
    // Direct term match - user said just the term or "explain [term]"
    const lowerTranscript = transcript.toLowerCase();

    // Try to match patterns like "explain blanching", "what is julienne", etc.
    const patterns = [
      /(?:explain|define|what is|what does|what's|help with|tell me about)\s+(?:a\s+)?(.+?)(?:\s+mean)?$/i,
      /(?:i don't understand|don't understand|i don't know what)\s+(.+?)(?:\s+means?)?$/i,
      /(.+?)\s+(?:what does that mean|what is that|explain that)$/i,
    ];

    for (const pattern of patterns) {
      const match = lowerTranscript.match(pattern);
      if (match) {
        const potentialTerm = match[1].trim().toLowerCase();
        // Check if it's a known term
        if (COOKING_TERMS[potentialTerm]) {
          return potentialTerm;
        }
        // Try partial match (e.g., "blanching" -> "blanch")
        for (const key of TERM_KEYS) {
          if (potentialTerm.includes(key) || key.includes(potentialTerm)) {
            return key;
          }
        }
      }
    }

    // Also check for standalone term mentions
    for (const key of TERM_KEYS) {
      const regex = new RegExp(`\\b${key}\\b`, 'i');
      if (regex.test(lowerTranscript)) {
        return key;
      }
    }

    return null;
  }, []);

  // Explain a cooking term via TTS
  const explainTerm = useCallback((termKey) => {
    const term = COOKING_TERMS[termKey];
    if (!term) return false;

    setExplainingTerm(termKey);

    // Cancel any current speech
    if ('speechSynthesis' in window) {
      speechSynthesis.cancel();
    }

    // Speak the explanation
    const text = `${term.term}. ${term.definition}`;
    if ('speechSynthesis' in window && !isMuted) {
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = 0.9;
      utterance.pitch = 1;
      utterance.onend = () => {
        setExplainingTerm(null);
      };
      speechSynthesis.speak(utterance);
    } else {
      // Clear after a few seconds if muted
      setTimeout(() => setExplainingTerm(null), 3000);
    }

    return true;
  }, [isMuted]);

  const instructions = recipe?.instructions || [];
  const currentInstruction = instructions[currentStep];
  const instructionText = typeof currentInstruction === 'string'
    ? currentInstruction
    : currentInstruction?.text || currentInstruction?.step || '';
  const detectedTimer = extractTimer(instructionText);

  // Handle "explain that" - find terms in current step and explain the first one
  const explainCurrentStepTerms = useCallback(() => {
    const terms = findTermsInText(instructionText);
    if (terms.length > 0) {
      explainTerm(terms[0]);
      return true;
    }
    // No terms found
    if ('speechSynthesis' in window && !isMuted) {
      const utterance = new SpeechSynthesisUtterance("I don't see any cooking terms in this step that I can explain.");
      utterance.rate = 0.9;
      speechSynthesis.speak(utterance);
    }
    return false;
  }, [instructionText, findTermsInText, explainTerm, isMuted]);

  // Request wake lock to keep screen on
  useEffect(() => {
    const requestWakeLock = async () => {
      try {
        if ('wakeLock' in navigator) {
          wakeLockRef.current = await navigator.wakeLock.request('screen');
        }
      } catch (err) {
        console.log('Wake lock not supported:', err);
      }
    };
    requestWakeLock();

    return () => {
      wakeLockRef.current?.release();
    };
  }, []);

  // Initialize speech synthesis and wait for voices
  useEffect(() => {
    if (!('speechSynthesis' in window)) return;

    const loadVoices = () => {
      const voices = speechSynthesis.getVoices();
      if (voices.length > 0) {
        setVoicesReady(true);
      }
    };

    // Try to load voices immediately
    loadVoices();

    // Chrome loads voices asynchronously
    speechSynthesis.onvoiceschanged = loadVoices;

    // Fallback: mark as ready after 1 second even if voices haven't loaded
    // (some browsers don't fire onvoiceschanged)
    const fallbackTimer = setTimeout(() => {
      setVoicesReady(true);
    }, 1000);

    return () => {
      speechSynthesis.onvoiceschanged = null;
      clearTimeout(fallbackTimer);
    };
  }, []);

  // Text-to-speech - pauses recognition to avoid hearing itself
  const speak = useCallback((text) => {
    if (isMuted || !text) return;
    if ('speechSynthesis' in window) {
      // Cancel any ongoing speech
      speechSynthesis.cancel();

      // Set speaking flag BEFORE stopping recognition
      // This prevents the onend handler from auto-restarting
      isSpeakingRef.current = true;

      // Pause recognition while speaking to avoid feedback loop
      if (recognitionRef.current && isListening) {
        try {
          recognitionRef.current.stop();
        } catch (e) {
          // Ignore stop errors
        }
      }

      // Small delay to ensure cancel completes
      setTimeout(() => {
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.rate = 0.9;
        utterance.pitch = 1;

        // Try to use a good English voice
        const voices = speechSynthesis.getVoices();
        const englishVoice = voices.find(v => v.lang.startsWith('en') && v.name.includes('Google')) ||
                            voices.find(v => v.lang.startsWith('en'));
        if (englishVoice) {
          utterance.voice = englishVoice;
        }

        // Resume recognition after speech ends
        utterance.onend = () => {
          isSpeakingRef.current = false;
          if (isListening && recognitionRef.current) {
            setTimeout(() => {
              try {
                recognitionRef.current.start();
              } catch (e) {
                console.log('Could not restart recognition after speech:', e);
              }
            }, 300); // Delay before resuming to avoid picking up tail end of speech
          }
        };

        utterance.onerror = () => {
          isSpeakingRef.current = false;
        };

        speechSynthesis.speak(utterance);
      }, 50);
    }
  }, [isMuted, isListening]);

  // Speak current instruction when step changes
  useEffect(() => {
    if (instructionText && voicesReady) {
      // Delay initial speak slightly to ensure everything is ready
      const delay = hasSpokenRef.current ? 0 : 300;
      const timer = setTimeout(() => {
        speak(`Step ${currentStep + 1}. ${instructionText}`);
        hasSpokenRef.current = true;
      }, delay);
      return () => clearTimeout(timer);
    }
  }, [currentStep, instructionText, speak, voicesReady]);

  // Voice recognition setup
  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      setVoiceSupported(false);
      return;
    }

    setVoiceSupported(true);
    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = false;
    recognition.lang = 'en-US';

    recognition.onresult = (event) => {
      const last = event.results[event.results.length - 1];
      if (last.isFinal) {
        const transcript = last[0].transcript.toLowerCase().trim();
        setLastCommand(transcript);
        handleVoiceCommand(transcript);
      }
    };

    recognition.onerror = (event) => {
      console.log('Speech recognition error:', event.error);
      if (event.error !== 'no-speech') {
        setIsListening(false);
      }
    };

    recognition.onend = () => {
      // Restart if still supposed to be listening AND not currently speaking
      // The isSpeakingRef check prevents auto-restart during TTS which causes feedback loops
      if (isListening && !isSpeakingRef.current) {
        try {
          recognition.start();
        } catch (e) {
          console.log('Could not restart recognition:', e);
        }
      }
    };

    recognitionRef.current = recognition;

    return () => {
      recognition.stop();
    };
  }, []);

  // Start/stop listening based on state
  useEffect(() => {
    if (!recognitionRef.current) return;

    if (isListening) {
      try {
        recognitionRef.current.start();
      } catch (e) {
        console.log('Recognition already started');
      }
    } else {
      recognitionRef.current.stop();
    }
  }, [isListening]);

  const handleVoiceCommand = useCallback((transcript) => {
    // First, check for term explanation requests
    const explainPhrases = [
      'explain that', 'what does that mean', "what's that mean", 'help me understand',
      "i don't understand", "don't understand that", 'what is that',
    ];

    // Check for context-free "explain that" type commands
    if (explainPhrases.some(p => transcript.includes(p))) {
      explainCurrentStepTerms();
      return;
    }

    // Check for specific term explanations like "explain blanching", "what is julienne"
    const termExplainPatterns = [
      /(?:explain|define|what is|what does|what's|help with|tell me about)\s+/i,
      /(?:i don't understand|don't understand|i don't know what)\s+/i,
    ];

    if (termExplainPatterns.some(p => p.test(transcript))) {
      const termKey = extractTermFromSpeech(transcript);
      if (termKey) {
        explainTerm(termKey);
        return;
      }
    }

    const commands = {
      next: ['next', 'next step', 'continue', 'forward'],
      previous: ['previous', 'back', 'go back', 'last step'],
      repeat: ['repeat', 'again', 'say again'],
      timer: ['start timer', 'timer', 'set timer', 'begin timer'],
      stopTimer: ['stop timer', 'pause timer', 'cancel timer'],
      stop: ['stop', 'exit', 'done', 'close', 'quit'],
    };

    for (const [action, phrases] of Object.entries(commands)) {
      if (phrases.some(p => transcript.includes(p))) {
        switch (action) {
          case 'next':
            // Inline navigation - go to next step
            setCurrentStep(prev => {
              const maxStep = instructions.length - 1;
              return prev < maxStep ? prev + 1 : prev;
            });
            break;
          case 'previous':
            // Inline navigation - go to previous step
            setCurrentStep(prev => prev > 0 ? prev - 1 : prev);
            break;
          case 'repeat':
            speak(`Step ${currentStep + 1}. ${instructionText}`);
            break;
          case 'timer':
            // Inline timer start
            if (detectedTimer && !timers[currentStep]?.isRunning) {
              setTimers(prev => ({
                ...prev,
                [currentStep]: {
                  total: detectedTimer,
                  remaining: detectedTimer,
                  isRunning: true,
                },
              }));
            }
            break;
          case 'stopTimer':
            // Inline timer pause
            if (timers[currentStep]?.isRunning) {
              setTimers(prev => ({
                ...prev,
                [currentStep]: {
                  ...prev[currentStep],
                  isRunning: false,
                },
              }));
            }
            break;
          case 'stop':
            onClose();
            break;
        }
        return;
      }
    }
  }, [currentStep, instructions.length, instructionText, detectedTimer, timers, speak, onClose, explainCurrentStepTerms, extractTermFromSpeech, explainTerm]);

  // Timer tick
  useEffect(() => {
    const interval = setInterval(() => {
      setTimers(prev => {
        const updated = { ...prev };
        for (const step of Object.keys(updated)) {
          if (updated[step].isRunning && updated[step].remaining > 0) {
            updated[step] = {
              ...updated[step],
              remaining: updated[step].remaining - 1,
            };
            // Timer finished
            if (updated[step].remaining === 0) {
              updated[step].isRunning = false;
              speak('Timer finished!');
            }
          }
        }
        return updated;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [speak]);

  const goToNext = useCallback(() => {
    if (currentStep < instructions.length - 1) {
      setCurrentStep(prev => prev + 1);
    }
  }, [currentStep, instructions.length]);

  const goToPrevious = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  }, [currentStep]);

  const markComplete = useCallback(() => {
    setCompletedSteps(prev => new Set([...prev, currentStep]));
    if (currentStep < instructions.length - 1) {
      setTimeout(() => setCurrentStep(prev => prev + 1), 300);
    }
  }, [currentStep, instructions.length]);

  const startTimer = useCallback(() => {
    if (!detectedTimer) return;
    setTimers(prev => ({
      ...prev,
      [currentStep]: {
        total: detectedTimer,
        remaining: detectedTimer,
        isRunning: true,
      },
    }));
  }, [detectedTimer, currentStep]);

  const pauseTimer = useCallback(() => {
    setTimers(prev => ({
      ...prev,
      [currentStep]: {
        ...prev[currentStep],
        isRunning: false,
      },
    }));
  }, [currentStep]);

  const resumeTimer = useCallback(() => {
    setTimers(prev => ({
      ...prev,
      [currentStep]: {
        ...prev[currentStep],
        isRunning: true,
      },
    }));
  }, [currentStep]);

  const resetTimer = useCallback(() => {
    if (!detectedTimer) return;
    setTimers(prev => ({
      ...prev,
      [currentStep]: {
        total: detectedTimer,
        remaining: detectedTimer,
        isRunning: false,
      },
    }));
  }, [detectedTimer, currentStep]);

  const currentTimer = timers[currentStep];
  const timerColor = currentTimer?.remaining <= 10 ? 'text-red-500' : 'text-primary-400';

  return (
    <div className="fixed inset-0 z-[60] bg-gray-900 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-700">
        <button onClick={onClose} className="p-2 text-white hover:bg-gray-700 rounded-lg">
          <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>
        <h1 className="text-white font-semibold text-lg truncate max-w-[50%]">{recipe?.title}</h1>
        <div className="flex items-center gap-2">
          {voiceSupported && (
            <button
              onClick={() => setIsListening(!isListening)}
              className={`p-2 rounded-lg ${isListening ? 'bg-primary-500 text-white' : 'text-gray-400 hover:bg-gray-700'}`}
              title={isListening ? 'Voice commands active' : 'Enable voice commands'}
            >
              <svg className={`w-6 h-6 ${isListening ? 'animate-pulse' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                <line x1="12" y1="19" x2="12" y2="23" />
                <line x1="8" y1="23" x2="16" y2="23" />
              </svg>
            </button>
          )}
          <button
            onClick={() => setIsMuted(!isMuted)}
            className={`p-2 rounded-lg ${isMuted ? 'text-red-400' : 'text-yellow-400'} hover:bg-gray-700`}
            title={isMuted ? 'Unmute' : 'Mute'}
          >
            {isMuted ? (
              <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                <line x1="23" y1="9" x2="17" y2="15" />
                <line x1="17" y1="9" x2="23" y2="15" />
              </svg>
            ) : (
              <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07" />
              </svg>
            )}
          </button>
        </div>
      </div>

      {/* Progress bar */}
      <div className="h-1 bg-gray-700">
        <div
          className="h-full bg-primary-500 transition-all duration-300"
          style={{ width: `${((currentStep + 1) / instructions.length) * 100}%` }}
        />
      </div>

      {/* Step counter */}
      <div className="text-center py-3 text-gray-400">
        Step {currentStep + 1} of {instructions.length}
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col items-center justify-center p-8 overflow-y-auto">
        {/* Step badge */}
        <div className="w-16 h-16 rounded-full bg-primary-500 flex items-center justify-center mb-6">
          <span className="text-white text-2xl font-bold">{currentStep + 1}</span>
        </div>

        {/* Instruction text */}
        <p className="text-white text-2xl md:text-3xl text-center leading-relaxed max-w-3xl">
          <HighlightedText text={instructionText} darkMode={true} />
        </p>

        {/* Timer section */}
        {detectedTimer && (
          <div className="mt-8 text-center">
            {currentTimer ? (
              <>
                <div className={`text-5xl font-mono ${timerColor} mb-4`}>
                  {formatTime(currentTimer.remaining)}
                </div>
                <div className="flex gap-3">
                  {currentTimer.isRunning ? (
                    <button
                      onClick={pauseTimer}
                      className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700"
                    >
                      Pause
                    </button>
                  ) : (
                    <button
                      onClick={resumeTimer}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                    >
                      Resume
                    </button>
                  )}
                  <button
                    onClick={resetTimer}
                    className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                  >
                    Reset
                  </button>
                </div>
              </>
            ) : (
              <button
                onClick={startTimer}
                className="px-6 py-3 bg-primary-500 text-white rounded-lg hover:bg-primary-600 flex items-center gap-2"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="12 6 12 12 16 14" />
                </svg>
                Start {formatTime(detectedTimer)} Timer
              </button>
            )}
          </div>
        )}

        {/* Term explanation indicator */}
        {explainingTerm && COOKING_TERMS[explainingTerm] && (
          <div className="mt-8 p-4 bg-primary-900/50 rounded-xl border border-primary-500/30 max-w-2xl">
            <div className="flex items-center gap-2 text-primary-400 mb-2">
              <svg className="w-5 h-5 animate-pulse" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>
              </svg>
              <span className="font-semibold">{COOKING_TERMS[explainingTerm].term}</span>
            </div>
            <p className="text-gray-300 text-lg">
              {COOKING_TERMS[explainingTerm].definition}
            </p>
          </div>
        )}

        {/* Voice command indicator */}
        {lastCommand && isListening && !explainingTerm && (
          <div className="mt-6 text-gray-500 text-sm">
            Heard: "{lastCommand}"
          </div>
        )}
      </div>

      {/* Navigation buttons */}
      <div className="flex items-center justify-center gap-6 p-6 border-t border-gray-700">
        <button
          onClick={goToPrevious}
          disabled={currentStep === 0}
          className="w-16 h-16 rounded-full bg-gray-700 text-white flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed hover:bg-gray-600"
        >
          <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>
        <button
          onClick={markComplete}
          className={`w-20 h-20 rounded-full flex items-center justify-center ${
            completedSteps.has(currentStep)
              ? 'bg-green-500 text-white'
              : 'bg-primary-500 text-white hover:bg-primary-600'
          }`}
        >
          {completedSteps.has(currentStep) ? (
            <svg className="w-10 h-10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          ) : (
            <svg className="w-10 h-10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
            </svg>
          )}
        </button>
        <button
          onClick={goToNext}
          disabled={currentStep === instructions.length - 1}
          className="w-16 h-16 rounded-full bg-gray-700 text-white flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed hover:bg-gray-600"
        >
          <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M9 18l6-6-6-6" />
          </svg>
        </button>
      </div>

      {/* Step dots */}
      <div className="flex justify-center gap-2 pb-6 overflow-x-auto px-4">
        {instructions.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentStep(index)}
            className={`w-3 h-3 rounded-full flex-shrink-0 transition-colors ${
              completedSteps.has(index)
                ? 'bg-green-500'
                : index === currentStep
                ? 'bg-primary-500 ring-2 ring-primary-300'
                : 'bg-gray-600'
            }`}
          />
        ))}
      </div>

      {/* Voice commands hint */}
      {voiceSupported && isListening && (
        <div className="text-center pb-4 text-gray-500 text-xs">
          Say: "next", "previous", "repeat", "explain [term]", "what does that mean", or "stop"
        </div>
      )}
    </div>
  );
}

export default CookingMode;
